import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MessageService } from '../../../core/service/message.service';
import { RentalObjectCategory } from '../../../rent/model/const/enums';
import { DwellingService } from '../../../rent/service/data/dwelling.service';
import { OtherRentalObjectService } from '../../../rent/service/data/other-rental-object.service';
import { ParkingService } from '../../../rent/service/data/parking.service';
import { PremisesService } from '../../../rent/service/data/premises.service';
import { ResidentialProperty } from '../../model/residential-property';
import { NonResidentialProperty } from '../../model/non-residential-property';
import { UserAccountService } from '../../../common/service/user-account.service';
import { map, Subject, takeUntil } from 'rxjs';
import { Features, ModuleNames } from '../../../common/model/account-definition';

const NEWS_POPUP_KEY = 'dinHyresvard.hideNewsPopup';
const NEWS_POPUP_DAYS = 90; // Number of days to hide the popup when user chooses "Do not show again"

@Component({
    selector: 'app-overview',
    templateUrl: './system-overview.component.html',
    styleUrls: ['./system-overview.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SystemOverviewComponent implements OnInit {
  private destroy$ = new Subject<void>();

  constructor(
    private userAccountService: UserAccountService,
    private dwellingService: DwellingService,
    private otherRenalObjectService: OtherRentalObjectService,
    private parkingService: ParkingService,
    private premisesService: PremisesService,
    private messageService: MessageService
  ) {}

  dwelling: ResidentialProperty = new ResidentialProperty({ category: RentalObjectCategory.Dwelling });
  otherRentalObject: NonResidentialProperty = new NonResidentialProperty({ category: RentalObjectCategory.Other });
  parking: NonResidentialProperty = new NonResidentialProperty({ category: RentalObjectCategory.Parking });
  premises: ResidentialProperty = new ResidentialProperty({ category: RentalObjectCategory.Premises });

  showNewsPopup: boolean = false;

  // To forcefully disable the news popup, set this value to true. If there is no headline, the popup will not be shown anyway.
  newsDisabled: boolean = true;

  // News popup content: Change this when there is a new news item to show. Empty these strings when there is no news to show. "newsHeadline" is used as key in local storage to identify the news item.
  newsHeadline: string = '';
  newsDescription: string = '';
  newsMediaUrl: string = '';
  newsMediaAltText: string = '';
  newsLinkUrl: string = '';
  newsLinkText: string = '';

  ngOnInit() {
    const allHiddenPopups = JSON.parse(localStorage.getItem(NEWS_POPUP_KEY) || '{}');
    let hidePopup = false;
    const popupInfo = allHiddenPopups[this.newsHeadline];
    if (popupInfo) {
      if (popupInfo.hide && Date.now() < popupInfo.expires) {
        hidePopup = true;
      } else if (Date.now() >= popupInfo.expires) {
        delete allHiddenPopups[this.newsHeadline];
        localStorage.setItem(NEWS_POPUP_KEY, JSON.stringify(allHiddenPopups));
      }
    }

    this.dwellingService.getStatistics().subscribe(
      data => {
        data.category = RentalObjectCategory.Dwelling;
        this.dwelling = new ResidentialProperty(data);
      },
      error => this.messageService.showError(error)
    );

    this.otherRenalObjectService.getStatistics().subscribe(
      data => {
        data.category = RentalObjectCategory.Other;
        this.otherRentalObject = new NonResidentialProperty(data);
      },
      error => this.messageService.showError(error)
    );

    this.parkingService.getStatistics().subscribe(
      data => {
        data.category = RentalObjectCategory.Parking;
        this.parking = new NonResidentialProperty(data);
      },
      error => this.messageService.showError(error)
    );

    this.premisesService.getStatistics().subscribe(
      data => {
        data.category = RentalObjectCategory.Premises;
        this.premises = new ResidentialProperty(data);
      },
      error => this.messageService.showError(error)
    );

    this.userAccountService.current
      .pipe(
        map(account => {
          if (account.hasAccess(ModuleNames.System, Features.Admin) && !hidePopup) this.showNewsPopup = true;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        error: error => this.messageService.showError(error),
      });
  }

  onNewsPopupClosed() {
    this.showNewsPopup = false;
  }

  onNewsPopupStopShowing(headline: string) {
    this.showNewsPopup = false;
    const expiry = new Date();
    expiry.setDate(expiry.getDate() + NEWS_POPUP_DAYS);

    // Get or create the object for all popups
    const allHiddenPopups = JSON.parse(localStorage.getItem(NEWS_POPUP_KEY) || '{}');
    allHiddenPopups[headline] = { hide: true, expires: expiry.getTime() };
    localStorage.setItem(NEWS_POPUP_KEY, JSON.stringify(allHiddenPopups));
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
